/**
 * O que o controle guarda, e como isso vira texto.
 *
 * Um arquivo só para não haver duas ideias de "vazio" e duas ideias de "como se
 * escreve uma data" espalhadas pelos onze subtipos.
 *
 * A forma do valor é contrato com o banco: `avancar` grava o jsonb como veio, e
 * `avaliar_condicao` compara o que está gravado com o que o organizador
 * escreveu na aresta. Mandar `true` onde a condição espera `"sim"` faz o ramo
 * nunca disparar — e o defeito só aparece no e-mail que não chegou.
 */

import { num, TRACO } from '../../util/formato';
import type { NoDoConvite, SubtipoPergunta, Valor } from '../../tipos';

/**
 * `sim_nao` grava texto, não booleano. É o que `docs/ESPECIFICACAO.md` §4.2
 * mostra na condição de exemplo (`"valor": "sim"`) e o que `servicos/api.ts`
 * documenta como um dos quatro casos de valor.
 */
export const SIM_NAO: readonly { readonly valor: string; readonly rotulo: string }[] = [
  { valor: 'sim', rotulo: 'Sim' },
  { valor: 'nao', rotulo: 'Não' },
];

/** Faixa padrão da escala quando o organizador não escreveu limites. */
export const ESCALA_MINIMA = 1;
export const ESCALA_MAXIMA = 5;

export type Limites = {
  readonly minimo: number | null;
  readonly maximo: number | null;
  readonly mascara: string | null;
};

function numeroDaConfig(bruto: unknown): number | null {
  if (typeof bruto === 'number' && Number.isFinite(bruto)) return bruto;
  if (typeof bruto === 'string' && bruto.trim() !== '') {
    const n = Number(bruto.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** `{minimo, maximo, mascara}` é tudo o que `no_para_convidado` libera. */
export function lerLimites(no: NoDoConvite): Limites {
  const mascara = no.config['mascara'];
  return {
    minimo: numeroDaConfig(no.config['minimo']),
    maximo: numeroDaConfig(no.config['maximo']),
    mascara: typeof mascara === 'string' && mascara.trim() !== '' ? mascara.trim() : null,
  };
}

/** Os degraus da escala, do mínimo ao máximo. Nunca vazio, nunca infinito. */
export function degrausDaEscala(no: NoDoConvite): readonly number[] {
  const { minimo, maximo } = lerLimites(no);
  const de = Math.round(minimo ?? ESCALA_MINIMA);
  const ate = Math.round(maximo ?? ESCALA_MAXIMA);
  // Faixa invertida ou absurda vira a padrão: uma escala de 400 botões é uma
  // tela que o convidado não rola até o fim.
  if (!(ate > de) || ate - de > 20) return degraus(ESCALA_MINIMA, ESCALA_MAXIMA);
  return degraus(de, ate);
}

function degraus(de: number, ate: number): readonly number[] {
  const saida: number[] = [];
  for (let i = de; i <= ate; i += 1) saida.push(i);
  return saida;
}

/** O que o controle mostra antes de o convidado tocar em nada. */
export function valorInicial(no: NoDoConvite): Valor {
  return no.subtipo === 'escolha_multipla' ? [] : null;
}

/**
 * Espelha `valor_vazio` do motor: nulo, texto em branco e lista sem itens são a
 * mesma coisa para quem responde. Serve só para desabilitar o que não faz
 * sentido enviar — **quem recusa a pergunta obrigatória é o banco**, com uma
 * mensagem que nomeia a pergunta.
 */
export function vazio(valor: Valor): boolean {
  if (valor === null || valor === undefined) return true;
  if (typeof valor === 'string') return valor.trim() === '';
  if (Array.isArray(valor)) return valor.length === 0;
  return false;
}

/** `aaaa-mm-dd` (o que `<input type="date">` devolve) vira `dd/mm/aaaa`.
 *
 *  Sem passar por `Date`: `new Date('2026-09-12')` é meia-noite UTC, e em
 *  Brasília isso é dia 11. Uma festa que "é dia 11" na confirmação de quem
 *  respondeu "dia 12" é o tipo de erro que ninguém atribui ao fuso. */
function dataLegivel(texto: string): string {
  const casado = /^(\d{4})-(\d{2})-(\d{2})$/.exec(texto);
  if (!casado) return texto;
  return `${casado[3]}/${casado[2]}/${casado[1]}`;
}

/** O que a trilha escreve. Nunca o valor cru: `["a","b"]` não é resposta. */
export function textoDoValor(valor: Valor, subtipo: SubtipoPergunta | null): string {
  if (vazio(valor)) return TRACO;

  if (Array.isArray(valor)) return valor.join(', ');

  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';

  if (typeof valor === 'number') return num(valor, Number.isInteger(valor) ? 0 : 2);

  const texto = String(valor).trim();

  if (subtipo === 'sim_nao') {
    const achada = SIM_NAO.find((o) => o.valor === texto);
    return achada ? achada.rotulo : texto;
  }

  if (subtipo === 'data') return dataLegivel(texto);

  return texto;
}

/**
 * Telefone brasileiro, formatado enquanto se digita. É a única máscara que
 * existe hoje (`config.mascara = "telefone"`, ver `0001_schema.sql`), e ela
 * existe porque no celular o teclado numérico não separa DDD de número —
 * quem digita onze dígitos seguidos não confere o que digitou.
 *
 * Formata o que couber e devolve o resto como está: máscara que apaga o que a
 * pessoa digitou é pior do que máscara nenhuma.
 */
export function formatarTelefone(bruto: string): string {
  const digitos = bruto.replace(/\D/g, '').slice(0, 11);
  if (digitos.length === 0) return '';
  if (digitos.length <= 2) return `(${digitos}`;
  const ddd = digitos.slice(0, 2);
  const resto = digitos.slice(2);
  if (resto.length <= 4) return `(${ddd}) ${resto}`;
  const corte = resto.length > 8 ? 5 : 4;
  return `(${ddd}) ${resto.slice(0, corte)}-${resto.slice(corte)}`;
}

/**
 * O texto do campo numérico vira número quando dá. Quando não dá, vai como
 * texto: `valor_numero` no banco entende vírgula decimal, e recusar aqui o que
 * o motor entende seria inventar uma regra a mais.
 */
export function numeroOuTexto(texto: string): Valor {
  const limpo = texto.trim();
  if (limpo === '') return null;
  const n = Number(limpo.replace(',', '.'));
  return Number.isFinite(n) ? n : limpo;
}
